export function initializeInteractions(document: Document, view: Window): () => void {
  const cleanup: Array<() => void> = [];
  const byId = <T extends Element = HTMLElement>(id: string): T | null =>
    document.getElementById(id) as T | null;
  const listen = (target: EventTarget | null, type: string, listener: EventListener): void => {
    if (!target) return;
    target.addEventListener(type, listener);
    cleanup.push(() => target.removeEventListener(type, listener));
  };

  const year = byId("copyright-year");
  if (year) year.textContent = String(new Date().getFullYear());

  const map = byId("discovery-map");
  const camera = byId<SVGElement>("parcel-camera");
  const selection = byId<SVGElement>("parcel-selection");
  const selectionBox = byId<SVGElement>("parcel-selection-box");
  const selectionCorners = byId<SVGElement>("parcel-selection-corners");
  const houses = map?.querySelectorAll<SVGElement>("[data-discovery-house]");
  const discoveryMetric = byId("discovery-metric");
  const discoveryStatus = byId("discovery-status");
  const motionControl = byId<HTMLButtonElement>("map-motion-toggle");
  const reduced = view.matchMedia?.("(prefers-reduced-motion: reduce)");
  const parcels = [
    {
      x: 242,
      y: 205,
      width: 115,
      height: 110,
      metric: "Estimated cash flow",
      detail: "+$350 / month",
    },
    {
      x: 375,
      y: 70,
      width: 103,
      height: 99,
      metric: "Estimated annual cash flow",
      detail: "+$4,200 / year",
    },
    {
      x: 242,
      y: 352,
      width: 115,
      height: 111,
      metric: "Cash-on-cash estimate",
      detail: "6.4%",
    },
    {
      x: 133,
      y: 205,
      width: 91,
      height: 110,
      metric: "Estimated monthly rent",
      detail: "$2,250 / month",
    },
    {
      x: 375,
      y: 205,
      width: 103,
      height: 110,
      metric: "Deal status",
      detail: "Shortlisted",
    },
  ];
  let parcelIndex = 0;
  let paused = false;
  let visible = true;
  let cycleTimer: number | undefined;
  let moveTimer: number | undefined;
  let settleTimer: number | undefined;
  const clearTimers = (): void => {
    [cycleTimer, moveTimer, settleTimer].forEach((timer) => {
      if (timer !== undefined) view.clearTimeout(timer);
    });
  };
  const allowed = (): boolean => !paused && !reduced?.matches && !document.hidden && visible;
  const position = (): void => {
    const parcel = parcels[parcelIndex];
    if (!parcel) return;
    // Center the actual lot, including the narrower and shorter parcels. Houses
    // stay fixed in map coordinates; only the camera and selection move.
    if (camera)
      camera.style.transform = `translate(${299.5 - parcel.x - parcel.width / 2}px,${260 - parcel.y - parcel.height / 2}px)`;
    selection?.setAttribute("transform", `translate(${parcel.x} ${parcel.y})`);
    selectionBox?.setAttribute("width", String(parcel.width));
    selectionBox?.setAttribute("height", String(parcel.height));
    selectionCorners?.setAttribute(
      "d",
      `M-3 8V-3H8M${parcel.width - 8} -3h11V8M${parcel.width + 3} ${parcel.height - 8}v11h-11M8 ${parcel.height + 3}H-3v-11`,
    );
    const selected = String(parcelIndex + 1).padStart(2, "0");
    if (map) map.dataset.parcel = selected;
    houses?.forEach((house) => {
      house.dataset.active = String(house.dataset.discoveryHouse === selected);
    });
    if (discoveryMetric) discoveryMetric.textContent = parcel.metric;
    if (discoveryStatus) discoveryStatus.textContent = parcel.detail;
  };
  const schedule = (): void => {
    if (cycleTimer !== undefined) view.clearTimeout(cycleTimer);
    if (allowed()) cycleTimer = view.setTimeout(discover, 5_000);
  };
  const discover = (): void => {
    if (!allowed()) return;
    map?.classList.add("is-searching");
    if (discoveryStatus) discoveryStatus.textContent = "Finding the next opportunity…";
    moveTimer = view.setTimeout(() => {
      parcelIndex = (parcelIndex + 1) % parcels.length;
      position();
    }, 260);
    settleTimer = view.setTimeout(() => {
      map?.classList.remove("is-searching");
    }, 1_100);
    schedule();
  };
  const syncMotion = (): void => {
    clearTimers();
    map?.classList.remove("is-searching");
    map?.classList.toggle("motion-paused", !allowed());
    position();
    motionControl?.setAttribute("aria-pressed", String(paused));
    motionControl?.setAttribute(
      "aria-label",
      paused ? "Resume property discovery animation" : "Pause property discovery animation",
    );
    schedule();
  };
  listen(motionControl, "click", () => {
    paused = !paused;
    syncMotion();
  });
  if (reduced) listen(reduced, "change", syncMotion);
  listen(document, "visibilitychange", syncMotion);
  listen(view, "pagehide", clearTimers);
  listen(view, "pageshow", syncMotion);
  let observer: IntersectionObserver | undefined;
  const Observer = (view as Window & { IntersectionObserver?: typeof IntersectionObserver })
    .IntersectionObserver;
  if (map && Observer) {
    const nextObserver = new Observer((entries: IntersectionObserverEntry[]) => {
      const next = entries[0]?.isIntersecting ?? true;
      if (next !== visible) {
        visible = next;
        syncMotion();
      }
    });
    nextObserver.observe(map);
    observer = nextObserver;
  }
  syncMotion();

  return () => {
    clearTimers();
    observer?.disconnect();
    cleanup.splice(0).forEach((remove) => remove());
  };
}
